import { useEffect, useId, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { searchApi, normalizeList, pathForEntityType } from "@/services/api";
import { useI18n } from "../i18n";
import { useLocalePath } from "../hooks";
import { cn } from "@/lib/utils";

/** AI Studio–style global search overlay — API-backed, not src/data. */
export function GlobalSearchModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t, locale } = useI18n();
  const base = useLocalePath();
  const navigate = useNavigate();
  const titleId = useId();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ReturnType<typeof normalizeList>>([]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !q.trim()) {
      setItems([]);
      setError(null);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(() => {
      setLoading(true);
      searchApi
        .query({ q: q.trim(), language: locale })
        .then((res) => {
          if (!cancelled) setItems(normalizeList(res.data.items));
        })
        .catch((e) => {
          if (!cancelled) setError(e instanceof Error ? e.message : t("common.error"));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [q, locale, open, t]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-ink/50 px-4 pt-[12vh] backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl border border-mist bg-sand shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-mist px-3 py-2">
          <Search className="size-5 shrink-0 text-ridge/50" aria-hidden />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("home.searchPlaceholder")}
            className="min-w-0 flex-1 bg-transparent py-2 text-ink outline-none"
            aria-label={t("nav.search")}
          />
          <button type="button" onClick={onClose} className="rounded p-2 text-ridge hover:bg-mist" aria-label="Close">
            <X className="size-5" />
          </button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-2">
          <p id={titleId} className="sr-only">
            {t("nav.search")}
          </p>
          {loading ? <p className="px-3 py-4 text-sm text-ridge">{t("common.loading")}</p> : null}
          {error ? <p className="px-3 py-4 text-sm text-red-700">{error}</p> : null}
          {!loading && !error && q.trim() && !items.length ? (
            <p className="px-3 py-4 text-sm text-ridge">{t("search.noResults")}</p>
          ) : null}
          <ul>
            {items.map((item) => (
              <li key={`${item.type}-${item.id}`}>
                <button
                  type="button"
                  className="flex w-full flex-col rounded-lg px-3 py-2 text-start hover:bg-mist/80"
                  onClick={() => {
                    onClose();
                    navigate(
                      `${base}/${pathForEntityType(String(item.type || "destination"))}/${item.slug}`,
                    );
                  }}
                >
                  <span className="text-xs uppercase tracking-wide text-ridge">{String(item.type)}</span>
                  <span className="font-semibold text-ink">{item.name}</span>
                  {item.city ? <span className="text-sm text-ridge/70">{item.city}</span> : null}
                </button>
              </li>
            ))}
          </ul>
          {q.trim() ? (
            <Link
              to={`${base}/search?q=${encodeURIComponent(q.trim())}`}
              onClick={onClose}
              className="mt-1 block rounded-lg px-3 py-2 text-sm font-semibold text-pine hover:bg-mist/80"
            >
              {t("nav.search")}: {q.trim()}
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function HeroSearch({ className }: { className?: string }) {
  const { t } = useI18n();
  const base = useLocalePath();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [type, setType] = useState("places");

  const types = ["places", "hotels", "restaurants", "activities", "tours", "events"] as const;

  return (
    <div className={cn("w-full", className)}>
      <div
        className="mb-3 flex gap-1 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label="Search categories"
      >
        {types.map((searchType) => (
          <button
            key={searchType}
            type="button"
            role="tab"
            aria-selected={type === searchType}
            onClick={() => setType(searchType)}
            className={cn(
              "shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm",
              type === searchType
                ? "bg-pine text-sand"
                : "bg-white/20 text-sand/80 hover:bg-white/30 hover:text-sand",
            )}
          >
            {t(`search.${searchType}`)}
          </button>
        ))}
      </div>
      <form
        className="relative"
        onSubmit={(e) => {
          e.preventDefault();
          const params = new URLSearchParams();
          if (query.trim()) params.set("q", query.trim());
          params.set("type", type);
          navigate(`${base}/search?${params.toString()}`);
        }}
      >
        <Search
          className="pointer-events-none absolute start-4 top-1/2 size-5 -translate-y-1/2 text-ridge/50"
          aria-hidden
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("home.searchPlaceholder")}
          className="w-full rounded-full border border-white/20 bg-white/95 py-3.5 pe-28 ps-12 text-ink shadow-lg shadow-ink/10 placeholder:text-ridge/50 focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/30 sm:py-4"
        />
        <button
          type="submit"
          className="absolute end-2 top-1/2 -translate-y-1/2 rounded-full bg-pine px-4 py-2 text-sm font-semibold text-sand hover:bg-pine/90"
        >
          {t("nav.search")}
        </button>
      </form>
    </div>
  );
}
